import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {PharmacyManagementSystemIntegrationStepsEnum} from '../../../../interfaces/PharmacyManagementSystemIntegrationStepsEnum';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AlertTypesEnum} from '../../../../../shared/interfaces/AlertTypesEnum';
import {Select, Store} from '@ngxs/store';
import {ProfileState} from '../../../../../shared/states/profile/profile.state';
import {Observable} from 'rxjs/internal/Observable';
// eslint-disable-next-line max-len
import {SubscribeToPharmacyManagementSystem, TestPharmacyManagementSystemConnection} from '../../../../../shared/states/profile/profile.actions';
import {IAlertMessage} from '../../../../../shared/interfaces/alert-message.interface';
import {take, withLatestFrom} from 'rxjs/operators';
import { IIntegrationConfiguration, IntegrationTypeEnum } from 'src/app/shared/interfaces/integration.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HttpStatusCode } from 'src/app/shared/interfaces/HttpStatusCode';
import { downloadFile } from 'src/app/shared/helpers/helpers';

@UntilDestroy()
@Component({
  selector: 'app-pms-pending-connection',
  templateUrl: './pms-pending-connection.component.html',
  styleUrls: ['.././pms-integration-stepper-shared.component.scss', './pms-pending-connection.component.scss'],
})
export class PmsPendingConnectionComponent implements OnInit {
  @Input() progress: PharmacyManagementSystemIntegrationStepsEnum;
  @Input() pharmacyManagementSystemIntegration: IIntegrationConfiguration;
  @Output() back: EventEmitter<PharmacyManagementSystemIntegrationStepsEnum> = new EventEmitter();
  @Output() next: EventEmitter<PharmacyManagementSystemIntegrationStepsEnum> = new EventEmitter();

  @Output() save: EventEmitter<any> = new EventEmitter();

  form: UntypedFormGroup;

  PharmacyManagementSystemIntegrationStepsEnum = PharmacyManagementSystemIntegrationStepsEnum;
  IntegrationTypeEnum = IntegrationTypeEnum;

  public integrationTypes: Array<{id: number, name: string}>;

  alertMessage: IAlertMessage;
  autohideDelay = 3;
  showMessage: boolean;
  AlertTypesEnum = AlertTypesEnum;

  @Select(ProfileState.loading) loading$: Observable<boolean | undefined>;
  @Select(ProfileState.success) success$: Observable<boolean | undefined>;
  @Select(ProfileState.testPharmacyManagementSystemConnectionResponseStatusCode) testPharmacyManagementSystemConnectionResponseStatusCode$: Observable<HttpStatusCode | undefined>;

  @ViewChild('apiKey') apiKey;
  @ViewChild('testConnectionButton') testConnectionButton;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    this.integrationTypes = Object.keys(IntegrationTypeEnum)
      .filter(e => !Number.isNaN(Number.parseInt(e, 10)))
      .map(e => {
        return {id: Number.parseInt(e, 10), name: IntegrationTypeEnum[e].toString()};
      });
  }
  
  ngOnInit(): void {
    this.form = this.fb.group({
      id: this.pharmacyManagementSystemIntegration?.id,
      pharmacyId: this.pharmacyManagementSystemIntegration?.pharmacyId,
      integrationType: IntegrationTypeEnum.LibertyRx,
      apiKey: [this.pharmacyManagementSystemIntegration?.apiKey ?? null,
        [Validators.required, Validators.minLength(7), Validators.maxLength(7), Validators.pattern(/^[0-9]{7}$/)]],
      isActive: this.pharmacyManagementSystemIntegration?.isActive ?? true,
      consentForm: null
    });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // if we're on the Finish step and the user changes something, roll back to the Test step
        if (this.progress === PharmacyManagementSystemIntegrationStepsEnum.Finish) {
          this.back.emit(PharmacyManagementSystemIntegrationStepsEnum.PendingConnection);
        }
      });
      
    if (this.pharmacyManagementSystemIntegration) {
      this.form.patchValue(this.pharmacyManagementSystemIntegration);
    }
  }

  showHelp(): void {
    window.open('https://c2keep.com/how-to-generate-your-liberty-software-api-key-for-c2-keep', '_blank');
  }

  testConnection(): void {
    if (this.form.valid) {
      this.store.dispatch(new TestPharmacyManagementSystemConnection(this.form.value))
        .pipe(take(1), withLatestFrom(this.testPharmacyManagementSystemConnectionResponseStatusCode$))
        .subscribe(([_, code]) => {
          if (code === HttpStatusCode.OK) {
            this.progress = PharmacyManagementSystemIntegrationStepsEnum.Finish;
            this.next.emit(PharmacyManagementSystemIntegrationStepsEnum.Finish);
            this.autohideDelay = 3; 
            this.alertMessage = { text: 'Success!', type: AlertTypesEnum.Success };
          } else if (code === HttpStatusCode.Unauthorized) {
            this.autohideDelay = 5;
            this.alertMessage = { text: 'Unable to connect.  Please check your API key is correct.', type: AlertTypesEnum.Error };
          } else if (code === HttpStatusCode.TooManyRequests) {
            this.autohideDelay = 7;
            this.alertMessage = { text: 'LibertyLink Service is not enabled. To enable this service go to System > Settings > Workflow Services > LibertyLink or contact your LibertyRx account manager.', type: AlertTypesEnum.Error };
          } else this.alertMessage = { text: 'Error connecting:  Please try again.', type: AlertTypesEnum.Error };

          this.showMessage = true;
        });
    }
  }

  finishConnection(): void {
    if (this.form.valid) {
      this.store.dispatch(new SubscribeToPharmacyManagementSystem(this.form.value))
        .pipe(take(1), withLatestFrom(this.success$))
        .subscribe(([_, success]) => {
          if (success) {
            this.save.emit(this.form.value);
          } else {
            this.alertMessage = {text: 'Error connecting:  Please try again.', type: AlertTypesEnum.Error};
          }

          this.showMessage = true;
        });
    }
  }
  backToConnection(): void {
    this.showMessage = false;
    this.form.enable();
    this.back.emit(PharmacyManagementSystemIntegrationStepsEnum.PendingConnection);
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  focusDropdown(name: string): void {
    setTimeout(() => {
      this[name].showDropdown = true;
      setTimeout(() => this[name].dropdownItemsQueryList?.toArray()[0]?.nativeElement.focus());
    });
  }

  focusButton(buttonName: string): void {
    setTimeout(() => this[buttonName]?.focus());
  }

  chosenPharmacyManagementSystem(option: IIntegrationConfiguration): void {
    setTimeout(() => this.focusInput('wanInput'), 200);
  }
}
